import type { FunctionComponent, ReactNode } from 'react';
import { createContext, useContext, useMemo, useState } from 'react';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import { ChevronDown, FileJson2 } from 'lucide-react';
import { toTitleCase } from 'string-ts';

import { checkIsUrl } from '@ballerine/common';

import type { CompanySanctionMatch } from './extract-sanction-tags';
import { ctw } from '@/common';
import {
  Badge,
  Button,
  buttonVariants,
  DefaultTableCell,
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TextWithNAFallback,
  WarningFilledSvg,
} from '@/components';
import { JsonDialog } from '@/components/molecules/JsonDialog/JsonDialog';
import { DataTable } from '@/components/organisms/DataTable/DataTable';
import { extractSanctionTags } from './extract-sanction-tags';

export type { CompanySanctionMatch };

export type CompanySanctionsData = {
  totalMatches: number;
  matches: CompanySanctionMatch[];
  fullReport?: unknown;
  dateOfCheck?: string;
};

export type CompanySanctionsLabels = {
  complianceCheck: string;
  totalMatches: string;
  match: string;
  matches: string;
  fullReport: string;
  viewFullReport: string;
  dateOfCheck: string;
  scanStatus: string;
  completed: string;
  matchName: string;
  hits: string;
  officialLists: string;
  reason: string;
  noResults: string;
  details: string;
  sources: string;
  alternativeNames: string;
  linkedIndividuals: string;
  address: string;
  emptyState: string;
  indexColumnHeader: string;
};

export const DEFAULT_COMPANY_SANCTIONS_LABELS = {
  complianceCheck: 'Compliance check',
  totalMatches: 'Total Matches',
  match: 'match',
  matches: 'matches',
  fullReport: 'Full Report',
  viewFullReport: 'View Information',
  dateOfCheck: 'Date Of Check',
  scanStatus: 'Scan Status',
  completed: 'Completed',
  matchName: 'Matched Name',
  hits: 'Hits',
  officialLists: 'Official Lists',
  reason: 'Reason',
  noResults: 'No results.',
  details: 'Details',
  sources: 'Sources',
  alternativeNames: 'Alternative Names',
  linkedIndividuals: 'Linked Individual',
  address: 'Address',
  emptyState: 'If applicable, company sanctions screening results will appear here once completed',
  indexColumnHeader: 'Match',
} satisfies CompanySanctionsLabels;

type CompanySanctionsContextValue = {
  data: CompanySanctionsData;
  expandedIndices: Set<number>;
  onToggleExpanded: (index: number) => void;
  labels: CompanySanctionsLabels;
};

const CompanySanctionsContext = createContext<CompanySanctionsContextValue | null>(null);

const useOptionalCompanySanctionsContext = () => useContext(CompanySanctionsContext);

const CompanySanctionsHeader: FunctionComponent<{
  title?: string;
  className?: string;
}> = ({ title: titleProp, className }) => {
  const ctx = useOptionalCompanySanctionsContext();
  const labels = ctx?.labels ?? DEFAULT_COMPANY_SANCTIONS_LABELS;

  return (
    <h3 className={ctw('my-4 block text-lg font-bold', className)}>
      {titleProp ?? labels.complianceCheck}
    </h3>
  );
};

const CompanySanctionsSummary: FunctionComponent<{
  totalMatches?: number;
  dateOfCheck?: string | undefined;
  fullReport?: unknown;
  tableClassName?: string;
}> = props => {
  const ctx = useOptionalCompanySanctionsContext();
  const labels = ctx?.labels ?? DEFAULT_COMPANY_SANCTIONS_LABELS;

  const totalMatches = props.totalMatches ?? ctx?.data.totalMatches ?? 0;
  const dateOfCheck = props.dateOfCheck ?? ctx?.data.dateOfCheck;
  const fullReport = props.fullReport ?? ctx?.data.fullReport;

  type SummaryRow = {
    totalMatches: number;
    fullReport: unknown;
    dateOfCheck: string | undefined;
    scanStatus: string;
  };
  const columnHelper = createColumnHelper<SummaryRow>();
  const columns = useMemo(
    () => [
      columnHelper.accessor('totalMatches', {
        header: labels.totalMatches,
        cell: info => {
          const value = info.getValue();
          const cellVariant = value === 0 ? 'success' : 'warning';

          return (
            <Badge variant={cellVariant} className="mb-1 rounded-lg px-2 py-1 font-bold">
              {value} {value === 1 ? labels.match : labels.matches}
            </Badge>
          );
        },
      }),
      columnHelper.accessor('fullReport', {
        header: labels.fullReport,
        cell: info => {
          const value = info.getValue();

          return (
            <div className="flex items-end justify-start">
              <JsonDialog
                buttonProps={{
                  variant: 'link',
                  className: 'p-0 text-blue-500 h-[unset]',
                }}
                rightIcon={<FileJson2 size="16" />}
                dialogButtonText={labels.viewFullReport}
                json={JSON.stringify(value)}
              />
            </div>
          );
        },
      }),
      columnHelper.accessor('dateOfCheck', {
        header: labels.dateOfCheck,
        cell: info => {
          const value = info.getValue();

          return (
            <TextWithNAFallback>
              {value ? dayjs(value).format('D MMM YYYY') : undefined}
            </TextWithNAFallback>
          );
        },
      }),
      columnHelper.accessor('scanStatus', {
        header: labels.scanStatus,
        cell: info => {
          const value = info.getValue();

          return (
            <Badge variant="success" className="mb-1 rounded-lg px-2 py-1 font-bold">
              {value}
            </Badge>
          );
        },
      }),
    ],
    [
      labels.totalMatches,
      labels.match,
      labels.matches,
      labels.fullReport,
      labels.viewFullReport,
      labels.dateOfCheck,
      labels.scanStatus,
    ],
  );

  const data = useMemo(
    () =>
      [
        {
          totalMatches,
          fullReport,
          dateOfCheck,
          scanStatus: labels.completed,
        },
      ] satisfies SummaryRow[],
    [totalMatches, fullReport, dateOfCheck, labels.completed],
  );

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: {
      cell: DefaultTableCell,
    },
  });

  return (
    <Table className={ctw(props.tableClassName)}>
      <TableHeader>
        {table.getHeaderGroups()?.map(headerGroup => (
          <TableRow key={headerGroup.id} className={ctw('hover:bg-unset border-none')}>
            {headerGroup.headers?.map((header, index) => (
              <TableHead
                key={header.id}
                className={ctw(
                  'text-foreground !h-[unset] !pl-0 pb-2 pt-0 text-sm font-medium leading-none',
                )}
              >
                {!header.isPlaceholder &&
                  flexRender(header.column.columnDef.header, header.getContext())}
              </TableHead>
            ))}
          </TableRow>
        ))}
      </TableHeader>
      <TableBody>
        {!!table.getRowModel().rows?.length &&
          table.getRowModel().rows?.map(row => (
            <TableRow key={row.id} className={ctw('hover:bg-unset h-6 border-none')}>
              {row.getVisibleCells()?.map(cell => (
                <TableCell key={cell.id} className={ctw('!py-px !pl-0')}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </TableCell>
              ))}
            </TableRow>
          ))}
      </TableBody>
    </Table>
  );
};

const CSMatchDetail: FunctionComponent<{ match: CompanySanctionMatch }> = ({ match }) => {
  const ctx = useOptionalCompanySanctionsContext();
  const labels = ctx?.labels ?? DEFAULT_COMPANY_SANCTIONS_LABELS;

  const validSources = useMemo(
    () => match?.sources?.filter(({ url }) => checkIsUrl(url)).map(({ url }) => url) ?? [],
    [match?.sources],
  );

  const alternativeNames = useMemo(
    () =>
      match?.alternativeNames?.map(item =>
        typeof item === 'string' ? item : `${item.name}${item.type ? ` (${item.type})` : ''}`,
      ) ?? [],
    [match?.alternativeNames],
  );

  const linkedIndividuals = useMemo(
    () =>
      match?.linkedIndividuals?.map(({ firstName, middleName, lastName, description }) => ({
        name: `${firstName ?? ''}${middleName ? ` ${middleName}` : ''} ${lastName ?? ''}`.trim(),
        description: description ?? '',
      })) ?? [],
    [match?.linkedIndividuals],
  );

  const addresses = useMemo(
    () =>
      match?.places?.map(({ country, city, address }) => ({
        fullAddress: [address, city, country].filter(Boolean).join(', ') || '',
      })) ?? [],
    [match?.places],
  );

  const officialLists = useMemo(
    () => match?.officialLists?.map(({ description }) => description) ?? [],
    [match?.officialLists],
  );

  return (
    <div className="flex flex-col gap-y-4">
      <h4 className="text-base font-semibold">{labels.details}</h4>

      {validSources.length > 0 && (
        <div className="flex gap-x-4">
          <span className="w-[150px] shrink-0 font-bold">{labels.sources}</span>
          <div className="flex flex-col gap-y-1">
            {validSources.map((url, index) => (
              <a
                key={index}
                className={buttonVariants({
                  variant: 'link',
                  className:
                    'h-[unset] cursor-pointer !justify-start break-all !p-0 text-left !text-blue-500',
                })}
                target="_blank"
                rel="noopener noreferrer"
                href={url}
              >
                {url}
              </a>
            ))}
          </div>
        </div>
      )}

      {alternativeNames.length > 0 && (
        <div className="flex gap-x-4">
          <span className="w-[150px] shrink-0 font-bold">{labels.alternativeNames}</span>
          <div className="flex flex-col gap-y-1">
            {alternativeNames.map((name, index) => (
              <TextWithNAFallback key={index} disableTranslation>
                {name}
              </TextWithNAFallback>
            ))}
          </div>
        </div>
      )}

      {linkedIndividuals.length > 0 && (
        <div className="flex gap-x-4">
          <span className="w-[150px] shrink-0 font-bold">{labels.linkedIndividuals}</span>
          <div className="flex flex-col gap-y-1">
            {linkedIndividuals.map(({ name, description }, index) => (
              <TextWithNAFallback key={index} disableTranslation>
                {name}
                {description && ` – ${description}`}
              </TextWithNAFallback>
            ))}
          </div>
        </div>
      )}

      {addresses.length > 0 && (
        <div className="flex gap-x-4">
          <span className="w-[150px] shrink-0 font-bold">{labels.address}</span>
          <div className="flex flex-col gap-y-1">
            {addresses.map(({ fullAddress }, index) => (
              <TextWithNAFallback key={index}>{fullAddress}</TextWithNAFallback>
            ))}
          </div>
        </div>
      )}

      {officialLists.length > 0 && (
        <div className="flex gap-x-4">
          <span className="w-[150px] shrink-0 font-bold">{labels.officialLists}</span>
          <div className="flex flex-col gap-y-1">
            {officialLists.map((list, index) => (
              <TextWithNAFallback key={index}>{list}</TextWithNAFallback>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

const CompanySanctionsMatches: FunctionComponent<{
  matches?: CompanySanctionMatch[];
  scrollClassName?: string;
  cellClassName?: string;
  CollapsibleContent?: FunctionComponent<{ row: CompanySanctionMatch }>;
}> = props => {
  const ctx = useOptionalCompanySanctionsContext();
  const labels = ctx?.labels ?? DEFAULT_COMPANY_SANCTIONS_LABELS;

  const matches = useMemo(
    () =>
      (props.matches ?? ctx?.data.matches ?? []).map((match, index) => ({
        ...match,
        id: match.id ?? `sanction-${index}`,
      })),
    [props.matches, ctx?.data.matches],
  );

  const columnHelper = createColumnHelper<CompanySanctionMatch>();

  const columns = useMemo(
    () => [
      columnHelper.display({
        id: 'collapsible',
        cell: ({ row }) => (
          <Button
            onClick={e => {
              e.stopPropagation();
              row.toggleExpanded();
            }}
            disabled={row.getCanExpand()}
            variant="ghost"
            size="icon"
            className="p-[7px]"
          >
            <ChevronDown
              className={ctw('d-4', {
                'rotate-180': row.getIsExpanded(),
              })}
            />
          </Button>
        ),
      }),
      columnHelper.display({
        id: 'index',
        cell: info => {
          const index = info.cell.row.index + 1;

          return <TextWithNAFallback className="p-1 font-semibold">{index}</TextWithNAFallback>;
        },
        header: labels.indexColumnHeader,
      }),
      columnHelper.accessor('primaryName', {
        header: labels.matchName,
        enableSorting: false,
        cell: info => {
          const value = info.getValue();

          return (
            <div className="max-w-[24ch] p-1">
              <TextWithNAFallback disableTranslation className="block truncate" title={value}>
                {value}
              </TextWithNAFallback>
            </div>
          );
        },
      }),
      columnHelper.display({
        id: 'hits',
        header: labels.hits,
        enableSorting: false,
        cell: info => {
          const tags = extractSanctionTags(info.row.original);

          if (tags.length === 0) {
            return (
              <div className="p-1">
                <TextWithNAFallback />
              </div>
            );
          }

          return (
            <div className="flex flex-wrap gap-2 p-1">
              {tags.map(tag => (
                <Badge
                  key={tag}
                  variant="warning"
                  className="cursor-pointer rounded-lg px-2 py-1 font-medium"
                >
                  {tag}
                </Badge>
              ))}
            </div>
          );
        },
      }),
      columnHelper.accessor('officialLists', {
        header: labels.officialLists,
        enableSorting: false,
        cell: info => {
          const officialLists = info.getValue();
          const count = officialLists?.length ?? 0;

          if (count === 0) {
            return (
              <div className="flex max-w-[9ch] justify-center">
                <TextWithNAFallback />
              </div>
            );
          }

          return (
            <div className="flex max-w-[9ch] justify-center">
              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <Badge variant="warning" className="mx-auto rounded-xl px-2 py-1 font-medium">
                    {count}
                  </Badge>
                </HoverCardTrigger>
                <HoverCardContent
                  align="start"
                  className="w-auto max-w-sm"
                  side="top"
                  collisionPadding={8}
                >
                  <div className="flex flex-col gap-2">
                    <h4 className="text-sm font-semibold">{labels.officialLists}</h4>
                    <div className="flex flex-col gap-1">
                      {officialLists?.map((list, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <WarningFilledSvg className="h-4 w-4 shrink-0 text-amber-500" />
                          <span className="text-sm">{list.description}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </div>
          );
        },
      }),
      columnHelper.accessor('reasonsForMatch', {
        header: labels.reason,
        enableSorting: false,
        cell: info => {
          const reasons = info.getValue();
          const firstReason = reasons?.[0];
          const value = toTitleCase(firstReason ?? '');

          return (
            <div className="max-w-[24ch] p-1">
              <TextWithNAFallback className="block truncate" title={value}>
                {value}
              </TextWithNAFallback>
            </div>
          );
        },
      }),
    ],
    [labels],
  );

  if (!matches || matches.length === 0) {
    return (
      <div className="rounded-lg border p-4 text-center text-slate-500">{labels.noResults}</div>
    );
  }

  const CollapsibleContent =
    props.CollapsibleContent ??
    (({ row }: { row: CompanySanctionMatch }) => <CSMatchDetail match={row} />);

  return (
    <DataTable
      data={matches}
      columns={columns}
      select={{
        onSelect: (_ids: Record<string, boolean>) => {},
        selected: {},
      }}
      CollapsibleContent={CollapsibleContent}
      props={{
        scroll: {
          className: ctw('[&>div]:max-h-[50vh]', props.scrollClassName, {
            'h-[100px]': matches.length === 0,
          }),
        },
        cell: {
          className: ctw('!p-0', props.cellClassName),
        },
        head: {
          className: 'pe-2',
        },
      }}
      options={{
        getRowId: (_row, index) => index?.toString() ?? '',
      }}
    />
  );
};

const CompanySanctionsEmptyState: FunctionComponent<{
  message?: string;
  className?: string;
}> = ({ message: messageProp, className }) => {
  const ctx = useOptionalCompanySanctionsContext();
  const labels = ctx?.labels ?? DEFAULT_COMPANY_SANCTIONS_LABELS;

  return (
    <p className={ctw('py-4 text-slate-500', className)}>{messageProp ?? labels.emptyState}</p>
  );
};

type CompanySanctionsComponentProps = {
  data?: CompanySanctionsData;
  expandedIndices?: Set<number>;
  onToggleExpanded?: (index: number) => void;
  labels?: Partial<CompanySanctionsLabels>;
  children?: ReactNode;
};

const CompanySanctionsComponent: FunctionComponent<CompanySanctionsComponentProps> = ({
  data: dataProp,
  expandedIndices: expandedIndicesProp,
  onToggleExpanded: onToggleExpandedProp,
  labels: labelsProp,
  children,
}) => {
  const [internalExpanded, setInternalExpanded] = useState<Set<number>>(new Set());

  const expandedIndices = expandedIndicesProp ?? internalExpanded;
  const onToggleExpanded =
    onToggleExpandedProp ??
    (index => {
      setInternalExpanded(prev => {
        const next = new Set(prev);

        if (next.has(index)) {
          next.delete(index);

          return next;
        }

        if (!next.has(index)) {
          next.add(index);

          return next;
        }

        return prev;
      });
    });

  const labels = useMemo(
    () => ({ ...DEFAULT_COMPANY_SANCTIONS_LABELS, ...labelsProp }),
    [labelsProp],
  );

  if (!dataProp) {
    return null;
  }

  const hasMatches = dataProp.matches?.length > 0;

  const ctxValue: CompanySanctionsContextValue = {
    data: dataProp,
    expandedIndices,
    onToggleExpanded,
    labels,
  };

  return (
    <CompanySanctionsContext.Provider value={ctxValue}>
      <div className="space-y-2">
        {children}
        {!children && (
          <>
            <CompanySanctionsHeader />
            {hasMatches && (
              <>
                <CompanySanctionsSummary />
                <CompanySanctionsMatches />
              </>
            )}
            {!hasMatches && <CompanySanctionsEmptyState />}
          </>
        )}
      </div>
    </CompanySanctionsContext.Provider>
  );
};

const CompanySanctionsWithSubComponents = Object.assign(CompanySanctionsComponent, {
  Header: CompanySanctionsHeader,
  Summary: CompanySanctionsSummary,
  Matches: CompanySanctionsMatches,
  MatchDetail: CSMatchDetail,
  EmptyState: CompanySanctionsEmptyState,
});

export const CompanySanctions = CompanySanctionsWithSubComponents;
